import { useAppDispatch, useAppSelector } from "@/core/app/hooks"
import {
  setOpenStlPrint,
  setOpenReviewPrint,
  clearPrintedStageInPrint,
} from "@/UDTreat/udTreatSlice"
import { UButton, UText } from "@/ui-component"
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from "@mui/material"
import React, { useLayoutEffect, useState } from "react"
import ReviewPrintStage, {
  reviewStageComponentHandle,
} from "./ReviewPrintStage"
import {
  attachmentModule,
  resourcesSynchronization,
  stagingManager,
  wasmModule,
} from "@/gluelayer"
import { set } from "react-hook-form"
import DialogBox from "@/UDTreat/bottom/DialogBox/dialogBox"
import JSZip from "jszip"
import { saveAs } from "file-saver"
import { RootState } from "@/core/app/store"
import { dateFormat } from "@/core/utils/formatters"
import { updateOrderPrint } from "@/core/app/slices/order/orderThunkApi"
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded"
import { updateCaseById } from "@/core/app/slices/case/caseThunkApi"
import { useParams } from "react-router-dom"
const ReviewPrint: React.FC = () => {
  const [downloadStatus, setDownloadStatus] = useState(false)
  const dispatch = useAppDispatch()
  const [showWarning, setShowWarning] = useState(false)
  const [showCutlineWarning, setShowCutlineWarning] = useState(false)

  const stageRef = React.useRef<reviewStageComponentHandle>(null)

  const {
    patientData: { firstName = "", lastName = "" },
  } = useAppSelector((state: RootState) => {
    return state.patientService
  })

  const { caseDetail } = useAppSelector((state: RootState) => state.caseService)
  const { printedStageInPrint, isPrintAsDPA } = useAppSelector(
    (state: RootState) => state.udTreatService,
  )

  const { patientId, caseId } = useParams()

  const downLoadFiles = () => {
    console.log("Download STL file")

    const modulePrintSelection =
      wasmModule.moduleManager.GetModulePrintSelection()
    if (!modulePrintSelection) return

    modulePrintSelection.SaveOutputFiles()

    const folderPath = modulePrintSelection.GetPrintResultFolder()
    console.log("🚀 ~ folder:", folderPath)
    const lastSegment = folderPath.split("/").pop()

    resourcesSynchronization
      .getFileListFromVirtualFS(lastSegment)
      .then((fileList: Record<string, File>) => {
        console.log("File List:", fileList)

        const zip = new JSZip()
        const entries = Object.entries(fileList)

        // 将每个文件添加到 zip 中
        entries.forEach(([filename, file]) => {
          const name = filename.split("/").pop() || filename
          zip.file(name, file)
        })

        // 获取当前时间作为 zip 文件名
        const now = new Date()

        const formatDate = dateFormat(now.toISOString(), "MMDD_HHmm")

        const zipFilename = `${firstName}_${lastName}_${formatDate}.zip`

        // 生成 zip 并下载
        zip
          .generateAsync({ type: "blob" })
          .then((zipBlob) => {
            saveAs(zipBlob, zipFilename)
            uploadOrderPrint()
          })
          .catch((err) => {
            console.error("生成 ZIP 失败:", err)
            setDownloadStatus(true)
          })
      })
      .catch((err) => {
        console.error("获取文件列表失败:", err)
        setDownloadStatus(true)
      })
  }
  const uploadOrderPrint = () => {
    const { up = [], low = [] } = printedStageInPrint

    const sortedUp = [...up].sort()
    const sortedLow = [...low].sort()

    const attachmentModule = wasmModule.statusController.GetAttachmentModel()
    const upTempIndex = attachmentModule.GetAttachmentFirstStage(
      wasmModule.module.ArchType.UpArch,
    )
    const lowerTempIndex = attachmentModule.GetAttachmentFirstStage(
      wasmModule.module.ArchType.DownArch,
    )
    const upStageCount = stagingManager.stageUpnNumber
    const lowerStageCount = stagingManager.stageLowNumber

    const udesign_json = JSON.parse(caseDetail.udesign_json)
    const newUdesignJsonObj = {
      ...udesign_json,
    }

    const upRange = []
    const upRetainer = []
    const upTemplate = []

    sortedUp.forEach((stage) => {
      const lPrint = newUdesignJsonObj.UpperPrintStageNum
      if (stage === 400) {
        upTemplate.push(400 + Math.max(0, upTempIndex - 1))
        newUdesignJsonObj.UpperTemplate = 1
      } else if (stage === 500) {
        upRetainer.push(500 + upStageCount)
        newUdesignJsonObj.UpperRetainer = 1
      } else {
        upRange.push(stage)
        if (lPrint < stage) newUdesignJsonObj.UpperPrintStageNum = stage
      }
    })

    const lowRange = []
    const lowRetainer = []
    const lowTemplate = []

    sortedLow.forEach((stage) => {
      const lPrint = newUdesignJsonObj.LowerPrintStageNum
      if (stage === 400) {
        lowTemplate.push(400 + Math.max(0, lowerTempIndex - 1))
        newUdesignJsonObj.LowerTemplate = 1
      } else if (stage === 500) {
        lowRetainer.push(500 + lowerStageCount)
        newUdesignJsonObj.LowerRetainer = 1
      } else {
        lowRange.push(stage)
        if (lPrint < stage) newUdesignJsonObj.LowerPrintStageNum = stage
      }
    })

    const payload = {
      caseId: String(caseDetail.id),
      type: isPrintAsDPA ? "Direct Print Aligner" : "In Office Order",
      upperStageRange: upRange.join(","),
      lowerStageRange: lowRange.join(","),
      upperStageCount: upRange.length,
      lowerStageCount: lowRange.length,
      upperRetainerStage: upRetainer.join(","),
      lowerRetainerStage: lowRetainer.join(","),
      upperTemplateStage: upTemplate.join(","),
      lowerTemplateStage: lowTemplate.join(","),
    }
    dispatch(updateOrderPrint(payload))
      .then((res) => {
        console.log("updateOrderPrint res:", res)
      })
      .finally(() => {
        setDownloadStatus(true)
      })

    dispatch(
      updateCaseById({
        patientId,
        caseId,
        payload: {
          case_extra_attrs: JSON.stringify(newUdesignJsonObj),
        },
      }),
    )
  }

  const clearWasmPrintMemory = () => {
    const printFolderName = "Print"
    resourcesSynchronization
      .getFileListFromVirtualFS(printFolderName)
      .then((fileList: Record<string, File>) => {
        console.log("🚀 ~ clearWasmPrintMemory ~ fileList:", fileList)
        Object.entries(fileList).forEach(([filename, file]) => {
          const path = "test/case/" + filename
          resourcesSynchronization.deleteFile(path)
        })
      })
    dispatch(setOpenReviewPrint(false))
    dispatch(clearPrintedStageInPrint())
    setDownloadStatus(false)
    const modulePrintSelection =
      wasmModule.moduleManager.GetModulePrintSelection()
    if (!modulePrintSelection) return
    modulePrintSelection.GoToOrigin3DScene()
  }

  const checkCutlineChanged = () => {
    const modulePrintSelection =
      wasmModule.moduleManager.GetModulePrintSelection()
    const cuttinglineChanged = modulePrintSelection.IsCuttinglineChanged()
    console.log("cuttinglineChanged:", cuttinglineChanged)
    if (cuttinglineChanged) {
      setShowCutlineWarning(true)
    } else {
      setShowWarning(true)
    }
  }
  useLayoutEffect(() => {
    return () => {
      clearWasmPrintMemory()
    }
  }, [])
  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: "100%",
      }}
    >
      {/* download successful show Box */}
      <Box
        id={"printSuccess"}
        sx={{
          width: "100%",
          padding: "100px 20%",
          display: downloadStatus ? "flex" : "none",
          flexDirection: "column",
        }}
      >
        <UText
          variant={"h5"}
          sxProp={{ padding: "8px 0" }}
        >{`Thank you for ordering STL Models`}</UText>
        <Box sx={{ margin: "16px 0", display: "flex", alignItems: "center" }}>
          <CheckCircleRoundedIcon sx={{ color: "green" }} />
          <span
            style={{
              color: "var(--text-primary, rgba(0, 0, 0, 0.87))",
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: 400,
              lineHeight: "175%" /* 28px */,
              letterSpacing: "0.15px",
            }}
          >{`Your prints have been downloaded.`}</span>
        </Box>
        <UText
          variant={"body2"}
        >{`Downloads can typically be found in your downloads folder.`}</UText>
      </Box>
      <Box
        sx={{
          width: "100%",
          // textAlign: "center",
          marginBottom: "24px",
          // marginTop: "30px",
          display: downloadStatus ? "none" : "block",
        }}
      >
        <UText variant={"h4"}>{`Print STL Model: Review`}</UText>
      </Box>
      {/* <Box
        sx={{
          //   background: "#fff",
          padding: "5px 10px",
          minHeight: "280px",
        }}
      >
        {" "}
      </Box> */}
      {downloadStatus ? <></> : <ReviewPrintStage ref={stageRef} />}

      <Stack
        sx={{
          height: "36px",
          width: "100%",
          display: downloadStatus ? "none" : "",
        }}
        direction={"row"}
        spacing={2}
        justifyContent="space-between"
      >
        <Button
          onClick={() => {
            clearWasmPrintMemory()
          }}
          variant="outlined"
          sx={{
            padding: "6px 16px",
            width: "auto",
            height: "36px",
            visibility: downloadStatus ? "hidden" : "visible",
          }}
        >
          {`<   BACK`}
        </Button>

        <Button
          onClick={() => {
            downloadStatus ? clearWasmPrintMemory() : checkCutlineChanged()
          }}
          variant="contained"
          sx={{
            padding: "6px 16px",
            width: "auto",
            height: "36px",
          }}
        >
          {downloadStatus ? `CONTINUE TO PRINT` : "OUTPUT AND SAVE STL FILES"}
        </Button>
      </Stack>

      <Dialog
        open={showWarning}
        // onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"You will be invoiced for exporting models"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="alert-dialog-description"
            sx={{ color: "black", marginBottom: "24px" }}
          >
            Would you like to proceed?
          </DialogContentText>
          <DialogContentText
            id="alert-dialog-description"
            sx={{ color: "black" }}
          >
            {`Please note you will not be charged for exporting the same model multiple times.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            sx={{
              color: "black",
              backgroundColor: "rgb(224,224,224)",
            }}
            onClick={() => setShowWarning(false)}
          >
            NO
          </Button>
          <Button
            variant="contained"
            disabled={!showWarning}
            onClick={() => {
              setShowWarning(false)
              setTimeout(() => {
                downLoadFiles()
              }, 0)
            }}
            autoFocus
          >
            Yes, Output
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={showCutlineWarning}
        // onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>
          <DialogContentText
            id="alert-dialog-description"
            sx={{ color: "black" }}
          >
            Do you want to save the change of cutting line?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            sx={{
              color: "black",
              backgroundColor: "rgb(224,224,224)",
            }}
            onClick={() => {
              stageRef.current?.resetCuttintlineFunc()
              setShowCutlineWarning(false)
              setShowWarning(true)
            }}
          >
            NO
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              const modulePrintSelection =
                wasmModule.moduleManager.GetModulePrintSelection()
              if (!modulePrintSelection) return
              modulePrintSelection.ApplyCuttinglineChanges()
              setShowCutlineWarning(false)
              setShowWarning(true)
            }}
            autoFocus
          >
            YES
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ReviewPrint
